import { fadeVolumeAt } from '../utils/localFileLevels';

const FADE_STEP_MS = 50;
const MAX_ACTIVE_SLOTS = 2;
const LOAD_ERROR_MESSAGE = 'Could not load the local file.';

export interface DeckEvents {
  onTimeLeft(requestId: string, msLeft: number): void;
  onEnded(requestId: string): void;
  onBlocked(requestId: string): void;
  onError(requestId: string, message: string): void;
}

export interface LocalFileDeck {
  preload(requestId: string, url: string): void;
  play(requestId: string, url: string, fadeInMs: number): Promise<void>;
  fadeOutAndStop(requestId: string, fadeMs: number): void;
  pause(requestId: string): void;
  resume(requestId: string): Promise<void>;
  stopAll(): void;
  isPlaying(requestId: string): boolean;
  retryBlocked(): Promise<void>;
}

interface Slot {
  requestId: string;
  audio: HTMLAudioElement;
  fadeTimer: ReturnType<typeof setInterval> | null;
  blocked: boolean;
  fadeInMs: number;
}

interface Preloaded {
  requestId: string;
  url: string;
  audio: HTMLAudioElement;
}

export function createLocalFileDeck(
  events: DeckEvents,
  makeAudio: (url: string) => HTMLAudioElement = (url) => new Audio(url),
): LocalFileDeck {
  let slots: Slot[] = [];
  let preloaded: Preloaded | null = null;

  const findSlot = (requestId: string) => slots.find((slot) => slot.requestId === requestId);
  const slotForAudio = (audio: HTMLAudioElement) => slots.find((slot) => slot.audio === audio);

  function cancelFade(slot: Slot) {
    if (slot.fadeTimer) {
      clearInterval(slot.fadeTimer);
      slot.fadeTimer = null;
    }
  }

  function releaseSlot(slot: Slot) {
    cancelFade(slot);
    slots = slots.filter((candidate) => candidate !== slot);
  }

  function stopSlot(slot: Slot) {
    releaseSlot(slot);
    slot.audio.pause();
  }

  function startFade(slot: Slot, from: number, to: number, durationMs: number, onDone?: () => void) {
    cancelFade(slot);
    const startedAt = Date.now();
    slot.audio.volume = fadeVolumeAt(0, durationMs, from, to);
    if (durationMs <= 0) {
      onDone?.();
      return;
    }
    slot.fadeTimer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      slot.audio.volume = fadeVolumeAt(elapsed, durationMs, from, to);
      if (elapsed >= durationMs) {
        cancelFade(slot);
        onDone?.();
      }
    }, FADE_STEP_MS);
  }

  function attachListeners(requestId: string, audio: HTMLAudioElement) {
    audio.addEventListener('timeupdate', () => {
      if (!slotForAudio(audio)) return;
      if (!Number.isFinite(audio.duration)) return;
      events.onTimeLeft(requestId, Math.max(0, (audio.duration - audio.currentTime) * 1000));
    });
    audio.addEventListener('ended', () => {
      const slot = slotForAudio(audio);
      if (!slot) return;
      releaseSlot(slot);
      events.onEnded(requestId);
    });
    audio.addEventListener('error', () => {
      const slot = slotForAudio(audio);
      if (!slot) return;
      releaseSlot(slot);
      events.onError(requestId, LOAD_ERROR_MESSAGE);
    });
  }

  async function startPlayback(slot: Slot): Promise<void> {
    try {
      await slot.audio.play();
    } catch (err) {
      if (!slots.includes(slot)) return;
      if (err instanceof Error && err.name === 'NotAllowedError') {
        slot.blocked = true;
        events.onBlocked(slot.requestId);
        return;
      }
      console.error('Local file playback failed:', err);
      releaseSlot(slot);
      events.onError(slot.requestId, LOAD_ERROR_MESSAGE);
      return;
    }
    slot.blocked = false;
    if (slot.fadeInMs > 0) startFade(slot, 0, 1, slot.fadeInMs);
  }

  return {
    preload(requestId, url) {
      if (findSlot(requestId)) return;
      if (preloaded?.requestId === requestId && preloaded.url === url) return;
      preloaded?.audio.pause();
      const audio = makeAudio(url);
      audio.preload = 'auto';
      attachListeners(requestId, audio);
      preloaded = { requestId, url, audio };
    },

    async play(requestId, url, fadeInMs) {
      if (findSlot(requestId)) return;

      let audio: HTMLAudioElement;
      if (preloaded?.requestId === requestId && preloaded.url === url) {
        audio = preloaded.audio;
        preloaded = null;
      } else {
        audio = makeAudio(url);
        attachListeners(requestId, audio);
      }

      while (slots.length >= MAX_ACTIVE_SLOTS) stopSlot(slots[0]);

      audio.volume = fadeInMs > 0 ? 0 : 1;
      const slot: Slot = { requestId, audio, fadeTimer: null, blocked: false, fadeInMs };
      slots.push(slot);
      await startPlayback(slot);
    },

    fadeOutAndStop(requestId, fadeMs) {
      const slot = findSlot(requestId);
      if (!slot) return;
      if (fadeMs <= 0) {
        stopSlot(slot);
        return;
      }
      startFade(slot, slot.audio.volume, 0, fadeMs, () => stopSlot(slot));
    },

    pause(requestId) {
      findSlot(requestId)?.audio.pause();
    },

    async resume(requestId) {
      const slot = findSlot(requestId);
      if (!slot) return;
      slot.fadeInMs = 0;
      await startPlayback(slot);
    },

    stopAll() {
      [...slots].forEach(stopSlot);
      preloaded?.audio.pause();
      preloaded = null;
    },

    isPlaying(requestId) {
      const slot = findSlot(requestId);
      return Boolean(slot && !slot.audio.paused);
    },

    async retryBlocked() {
      await Promise.all(slots.filter((slot) => slot.blocked).map(startPlayback));
    },
  };
}
